import { type Registry as Services, service } from '@ember/service';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { HomeawayConnectionValidation } from 'uplisting-frontend/validations/homeaway-connection';

const CHANNEL_CONNECTIONS_PERMISSION = 'connect.channel_connections.view';

type HomeawayConnection = ModelRegistry['homeaway-connection'];
type HomeawayLink = ModelRegistry['homeaway-link'];

export default class HomeawayConnectionRepositoryService extends BaseRepositoryService<HomeawayConnection> {
  @service session!: Services['session'];

  recordName = 'homeaway-connection';
  implementMethods = ['buildChangeset', 'findAll', 'unloadAll'];

  validation = HomeawayConnectionValidation;

  public async findAllForLinking(): Promise<HomeawayConnection[]> {
    if (
      !this.session.currentUser?.hasAccessTo(CHANNEL_CONNECTIONS_PERMISSION)
    ) {
      return [];
    }

    try {
      const connections = await this.store.findAll('homeaway-connection', {
        reload: true,
      });

      return connections.slice();
    } catch (error) {
      this.sentry.captureException(error);

      return [];
    }
  }

  public namedConnections(
    connections: HomeawayConnection[],
  ): HomeawayConnection[] {
    return connections.filter((connection) => !!connection.accountName);
  }

  public publishableConnections(
    connections: HomeawayConnection[],
  ): HomeawayConnection[] {
    return this.namedConnections(connections).filter(
      (connection) => connection.isPublishable,
    );
  }

  public assignDefaultConnection(
    link: HomeawayLink,
    connections: HomeawayConnection[],
  ): void {
    if (link.id || link.homeawayConnectionId) {
      return;
    }

    const [onlyConnection, ...otherConnections] =
      this.publishableConnections(connections);

    if (onlyConnection && otherConnections.length === 0) {
      link.homeawayConnectionId = Number(onlyConnection.id);
    }
  }

  public isConnectionChoiceRequired(
    link: HomeawayLink,
    connections: HomeawayConnection[],
  ): boolean {
    return (
      !link.id &&
      !link.homeawayConnectionId &&
      this.publishableConnections(connections).length > 1
    );
  }

  public isAwaitingPublishableConnection(
    link: HomeawayLink,
    connections: HomeawayConnection[],
  ): boolean {
    return (
      !link.id &&
      this.namedConnections(connections).length > 0 &&
      this.publishableConnections(connections).length === 0
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/homeaway-connection': HomeawayConnectionRepositoryService;
  }
}
